/**
 * RecentTransactionsList — last N transactions from
 * ``/banking/transactions`` rendered as :class:`ListItemRow` rows so
 * the operator sees REAL movements (not just aggregates) the moment
 * they land on the Banca tab.
 *
 * Visual rules
 * ------------
 *  - avatar tint = inflow (emerald) vs outflow (rose) so the eye reads
 *    the direction before the digits
 *  - amount on the right tinted with the same direction
 *  - eyebrow shows the category (or "Da classificare") + booking date
 *  - title = counterparty_name or "Senza controparte"
 *  - preview = description (cleaned of provider boilerplate)
 *  - "Ricorrente" pill when ``is_recurring === true``
 */

import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import type * as React from "react";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  ListItemEyebrow,
  ListItemRow,
  SenderAvatar,
} from "@ando/ui";

import type { BankTransactionDTO } from "@ando/core";


function formatAmount(cents: number, currency: string): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
    signDisplay: "auto",
  }).format(cents / 100);
}


function formatBookingDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "short",
  })
    .format(d)
    .replace(".", "");
}


/** Truncate provider boilerplate prefixes from the description. */
function cleanDescription(raw: string | null): string | null {
  if (!raw) return null;
  const trimmed = raw.replace(/^\s*(SEPA|BONIF|POS|PAG|ADDEB|ACCRED)[A-Z\s]+:?\s*/i, "");
  return trimmed.length > 0 ? trimmed : raw;
}


export interface RecentTransactionsListProps {
  readonly transactions: readonly BankTransactionDTO[];
}


export const RecentTransactionsList: React.FC<RecentTransactionsListProps> = ({
  transactions,
}) => {
  const { t } = useLingui();
  if (transactions.length === 0) return null;
  return (
    <ul className="-mx-5 flex flex-col" aria-label={t`Movimenti recenti`}>
      {transactions.map((tx, idx) => {
        const isOutflow = tx.amount_cents < 0;
        const Glyph = isOutflow ? ArrowUpRight : ArrowDownLeft;
        const toneBg = isOutflow ? "bg-rose-50" : "bg-emerald-50";
        const toneFg = isOutflow ? "text-rose-700" : "text-emerald-700";
        const description = cleanDescription(tx.description);
        const title = tx.counterparty_name?.trim() || description || t`Movimento`;
        const preview =
          tx.counterparty_name && description && description !== tx.counterparty_name
            ? description
            : null;
        return (
          <ListItemRow
            key={tx.id}
            itemId={tx.id}
            index={idx}
            avatar={
              <SenderAvatar
                toneBg={toneBg}
                toneFg={toneFg}
                size={28}
                icon={<Glyph size={14} strokeWidth={2} aria-hidden />}
                title={isOutflow ? t`Uscita` : t`Entrata`}
                className="mt-0.5"
              />
            }
            eyebrow={
              <>
                <ListItemEyebrow>
                  {tx.category ?? t`Da classificare`}
                </ListItemEyebrow>
                <span className="text-[10px] text-ink-3 tabular-nums">
                  {formatBookingDate(tx.booking_date)}
                </span>
              </>
            }
            title={title}
            preview={preview}
            trailing={
              <span
                className={
                  isOutflow
                    ? "font-semibold text-[13px] text-rose-700 tabular-nums"
                    : "font-semibold text-[13px] text-emerald-700 tabular-nums"
                }
              >
                {formatAmount(tx.amount_cents, tx.currency)}
              </span>
            }
            pills={
              tx.is_recurring ? (
                <span className="inline-flex items-center rounded-full border border-hairline bg-ink-1/[0.05] px-1.5 py-0.5 font-medium text-[10px] text-ink-3 tracking-[0.05em]">
                  <Trans>Ricorrente</Trans>
                </span>
              ) : null
            }
            onClick={() => {
              /* No detail route yet — clicking a transaction is a no-op
                 until the BE ships a per-tx view (or a journal entry
                 link via the categorizer). */
            }}
          />
        );
      })}
    </ul>
  );
};
RecentTransactionsList.displayName = "RecentTransactionsList";
